import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/AdminLayout.css';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  showSidebar?: boolean;
}

function AdminLayout({ children, title = "لوحة التحكم", showSidebar = true }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('fotomista_token');
    localStorage.removeItem('fotomista_user');
    localStorage.removeItem('adminAuthenticated');
    navigate('/admin-login');
  };

  const menuItems = [
    { 
      path: '/admin', 
      icon: '📊', 
      label: 'الرئيسية',
      description: 'نظرة عامة'
    },
    { 
      path: '/admin/bookings-management', 
      icon: '📋', 
      label: 'الحجوزات',
      description: 'إدارة الحجوزات'
    },
    { 
      path: '/admin/pages-management', 
      icon: '⚙️', 
      label: 'الإعدادات',
      description: 'إعدادات الموقع'
    },
    { 
      path: '/admin/cities-management', 
      icon: '🏙️', 
      label: 'المدن',
      description: 'إدارة المدن'
    },
    { 
      path: '/admin/promo-codes', 
      icon: '🎟️', 
      label: 'أكواد الخصم',
      description: 'إدارة العروض'
    },
    { 
      path: '/admin/invoices', 
      icon: '🧾', 
      label: 'الفواتير',
      description: 'إدارة الفواتير'
    },
    { 
      path: '/admin/email-management', 
      icon: '📧', 
      label: 'البريد الإلكتروني',
      description: 'إدارة الرسائل'
    },
    { 
      path: '/admin/calendar-view', 
      icon: '🗓️', 
      label: 'جدول الحجوزات',
      description: 'استعراض الحجوزات'
    },
    { 
      path: '/admin/calendar-management', 
      icon: '⚙️', 
      label: 'تقويم جوجل',
      description: 'إعدادات الربط'
    },
  ];

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="admin-layout">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            {showSidebar && (
              <button 
                className="sidebar-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                <span className={`hamburger ${sidebarCollapsed ? 'collapsed' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
            )}
            <h1 className="admin-title">{title}</h1>
          </div>
          
          <div className="admin-header-right">
            <Link to="/" className="admin-home-btn" title="العودة للموقع">
              <span>🏠</span>
            </Link>
            <button onClick={handleLogout} className="admin-logout-btn" title="تسجيل خروج">
              <span>↗</span>
            </button>
          </div>
        </div>
      </header>

      <div className="admin-body">
        {/* Sidebar */}
        {showSidebar && (
          <motion.aside 
            className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
            animate={{ width: sidebarCollapsed ? 64 : 240 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <nav className="admin-nav">
              {menuItems.map((item) => (
                <motion.div key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <motion.div 
                      className="nav-content"
                      animate={{ 
                        opacity: sidebarCollapsed ? 0 : 1,
                        width: sidebarCollapsed ? 0 : 'auto'
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-description">{item.description}</span>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.aside>
        )}

        {/* Main Content */}
        <main className={`admin-main ${!showSidebar ? 'full-width' : ''}`}>
          <div className="admin-content">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;